import {
  resolvePregnancyStands,
  type LegacyPregnancyOutcome,
} from "../pregnancy/resolvePregnancyStatus";
/** A standing positive may conclude in a birth without rewriting its outcome.
 * Patient statements take precedence; intake/import subtypes remain the fallback.
 * Stillbirth and other losses never reach celebratory copy.
 */

import type { CyclePregnancyStatusRecord } from "../pregnancy/useCyclePregnancyStatus";
import { resolvePostProcedureOutcomeClinicalState } from "../../cycles/presenters/postProcedureOutcomeState";

/**
 * The outcome fields this reads, named as the cycle API names them.
 *
 * Includes legacy outcome subtypes written by intake and clinic imports.
 */
export type RecordedBirthOutcome = LegacyPregnancyOutcome & {
  type?: string | null;
  resultStatus?: string | null;
  /** `entered_in_error` — the result was retracted rather than corrected. */
  recordStatus?: string | null;
  verificationStatus?: string | null;
};

/** Her own statement about the pregnancy, when she has made one. */
export type RecordedBirthPatientStatus = Pick<
  CyclePregnancyStatusRecord,
  "status" | "birthType" | "liveBirthCount"
>;

export type RecordedBirth = {
  /**
   * More than one baby.
   *
   * Read from the kind first, because that is what the recording writes: the
   * update step sends `multiple_live_birth` when the count is above one. The
   * count is honoured too, so a record that carries a number without the plural
   * kind — a correction, or a later backfill — still reads as one.
   */
  isMultiple: boolean;
  /** The number the record states, or null when she did not say. */
  liveBirthCount: number | null;
};

const LIVE_BIRTH_KINDS: ReadonlySet<string> = new Set([
  "live_birth",
  "multiple_live_birth",
]);

/**
 * Whether this cycle has reached a birth.
 *
 * The clinical result must stand. Resolve the birth from the patient statement
 * when present, or the legacy outcome subtype when there is no statement.
 *
 * Returns null for everything else, including a pregnancy that is still ongoing:
 * the caller then keeps the copy it already had.
 *
 * `resolvePostProcedureOutcomeClinicalState` is asked first, and it is the same
 * authority every other reader of this record uses. It covers the ways a positive
 * stops being one — retracted as entered in error, refuted or conflicting,
 * cancelled, withdrawn — and a birth reported against a result in any of those
 * states must not be celebrated, because the result it belongs to is no longer
 * standing.
 */
export function resolveRecordedBirth(
  outcome: RecordedBirthOutcome | null | undefined,
  patientStatus?: RecordedBirthPatientStatus | "unknown" | null,
): RecordedBirth | null {
  if (!outcome) return null;
  if (resolvePostProcedureOutcomeClinicalState(outcome) !== "pregnancy") {
    return null;
  }
  if (patientStatus === "unknown") return null;
  if (resolvePregnancyStands(outcome, patientStatus?.status) !== "birth") {
    return null;
  }
  const birthType = patientStatus
    ? patientStatus.birthType
    : outcome.pregnancyOutcome;
  if (birthType && !LIVE_BIRTH_KINDS.has(birthType)) return null;
  const statedCount = patientStatus
    ? patientStatus.liveBirthCount
    : outcome.liveBirthCount;
  const count =
    typeof statedCount === "number" && statedCount > 0 ? statedCount : null;

  return {
    isMultiple:
      birthType === "multiple_live_birth" || (count !== null && count > 1),
    liveBirthCount: count,
  };
}

const I18N_ROOT = "homepage.recorded_birth";

/**
 * The copy a recorded birth is announced in.
 *
 * Returns keys rather than strings, the way `resolveCycleClosingCopy` does, so
 * the mapping can be asserted without a translation function and so both
 * locales are reached through the same call at render.
 *
 * The supporting voice is not a nicety. A partner sees this cycle too, and
 * closing it belongs to the person who carried it — the recorded-result card
 * already says so ("Closing the cycle is theirs to do"). Told in her words, the
 * hero would hand him an instruction that is not his, so his body congratulates
 * them both and says nothing about closing.
 */
export function recordedBirthCopyKeys(input: {
  birth: RecordedBirth;
  viewerIsSupporting: boolean;
}): { titleKey: string; bodyKey: string } {
  const title = input.birth.isMultiple ? "babies_were_born" : "a_baby_was_born";
  const body = input.viewerIsSupporting
    ? "supporting.congratulations_to_you_both"
    : "congratulations_close_whenever_you_are_ready";

  return {
    titleKey: `${I18N_ROOT}.${title}`,
    bodyKey: `${I18N_ROOT}.${body}`,
  };
}

/**
 * The copy the closing card at the foot of Home is given after a birth.
 *
 * That card is the page's last word, and on a positive result it is about the
 * result's future: "What comes next, at your pace", a checklist item reading
 * "Review follow-up timing", and a chat seeded with "help me prepare for
 * follow-up after my positive result". After a birth the follow-up it means has
 * already happened. Its secondary action was the pregnancy-tracking setup, which
 * is the same offer the hero stopped making.
 *
 * What is still true is what the card says here: the record stays hers, the cycle
 * joins her history when she closes it, and Louise will hold a question until she
 * wants to ask it. Nothing is addressed only to the person who carried the cycle,
 * so this needs no supporting voice — the closing line describes what happens
 * rather than telling anyone to do it.
 */
export function recordedBirthHandoffCopyKeys(): {
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  checklistKeys: readonly [string, string];
  chatPromptKey: string;
} {
  return {
    titleKey: `${I18N_ROOT}.keep_this_with_your_records`,
    bodyKey: `${I18N_ROOT}.the_record_stays_and_louise_holds_your_questions`,
    ctaKey: `${I18N_ROOT}.ask_louise_your_questions`,
    checklistKeys: [
      `${I18N_ROOT}.the_result_stays_with_your_records`,
      `${I18N_ROOT}.the_cycle_joins_your_history_once_closed`,
    ],
    chatPromptKey: `${I18N_ROOT}.chat_prompt_after_a_birth`,
  };
}
